import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Car, Plus, LogIn, LogOut, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { del, get, patch, post, getErrorMessage } from '@/lib/api';
import { subscribe } from '@/lib/socket';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';

type VehicleKind = 'car' | 'bike' | 'cycle' | 'ev' | 'commercial' | 'other';
type VehicleEntryStatus = 'inside' | 'exited';
type VehicleEntryActor = 'resident' | 'visitor' | 'delivery' | 'service';

interface UnitLite {
  id: string;
  number: string;
  block: { id: string; name: string } | null;
}
interface ResidentLite {
  id: string;
  full_name: string;
  phone: string;
}
interface Vehicle {
  id: string;
  society_id: string;
  unit_id: string | null;
  resident_id: string | null;
  number_plate: string;
  kind: VehicleKind;
  make_model: string | null;
  color: string | null;
  parking_slot: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  unit: UnitLite | null;
  resident: ResidentLite | null;
}
interface VehicleEntry {
  id: string;
  society_id: string;
  vehicle_id: string | null;
  number_plate: string;
  actor_kind: VehicleEntryActor;
  visitor_name: string | null;
  unit_id: string | null;
  guard_id: string | null;
  gate_id: string | null;
  status: VehicleEntryStatus;
  entered_at: string;
  exited_at: string | null;
  notes: string | null;
  vehicle: { id: string; number_plate: string; kind: VehicleKind; color: string | null } | null;
  unit: UnitLite | null;
  gate: { id: string; name: string } | null;
}
interface VehicleListResponse {
  items: Vehicle[];
  next_cursor: string | null;
}
interface EntryListResponse {
  items: VehicleEntry[];
  next_cursor: string | null;
  inside_count: number;
}

const KIND_LABEL: Record<VehicleKind, string> = {
  car: 'Car',
  bike: 'Bike',
  cycle: 'Cycle',
  ev: 'EV',
  commercial: 'Commercial',
  other: 'Other'
};

const ACTOR_LABEL: Record<VehicleEntryActor, string> = {
  resident: 'Resident',
  visitor: 'Visitor',
  delivery: 'Delivery',
  service: 'Service'
};

const CreateVehicleSchema = z.object({
  number_plate: z.string().min(3, 'Plate required').max(20),
  kind: z.enum(['car', 'bike', 'cycle', 'ev', 'commercial', 'other']),
  unit_id: z.string().uuid().optional().or(z.literal('')),
  make_model: z.string().max(80).optional(),
  color: z.string().max(40).optional(),
  parking_slot: z.string().max(40).optional(),
  notes: z.string().max(300).optional()
});
type CreateVehicleForm = z.infer<typeof CreateVehicleSchema>;

interface UnitListResponse {
  items: { id: string; number: string; block: { id: string; name: string } | null }[];
}

function normalisePlateClient(s: string): string {
  return s.replace(/[\s-]+/g, '').toUpperCase();
}

export function VehiclesPage(): JSX.Element {
  const [tab, setTab] = useState<'registered' | 'entries'>('registered');
  return (
    <>
      <PageHeader
        title="Vehicles"
        description="Resident vehicles registered against units, plus live entry log from the gates."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <div className="flex items-center gap-vt-2 border-b border-vt-border">
          <TabBtn active={tab === 'registered'} onClick={() => setTab('registered')}>
            Registered
          </TabBtn>
          <TabBtn active={tab === 'entries'} onClick={() => setTab('entries')}>
            Entry log
          </TabBtn>
        </div>
        {tab === 'registered' ? <RegisteredTab /> : <EntriesTab />}
      </div>
    </>
  );
}

function TabBtn({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'border-b-2 border-vt-primary px-vt-4 py-vt-2 text-vt-14 font-semibold text-vt-primary'
          : 'border-b-2 border-transparent px-vt-4 py-vt-2 text-vt-14 text-vt-text-muted hover:text-vt-text'
      }
    >
      {children}
    </button>
  );
}

function RegisteredTab(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Vehicle | null>(null);
  const [kindFilter, setKindFilter] = useState<'' | VehicleKind>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const filtersHash = useMemo(
    () => JSON.stringify([search, activeFilter]),
    [search, activeFilter]
  );
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const query = useQuery({
    queryKey: ['vehicles', 'list', search, activeFilter, cursor],
    queryFn: () =>
      get<VehicleListResponse>('/vehicles', {
        params: {
          limit: 50,
          ...(search ? { q: search } : {}),
          ...(activeFilter === 'active' ? { active: true } : activeFilter === 'inactive' ? { active: false } : {}),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<Vehicle>(query.data, cursor, filtersHash);

  const unitsQuery = useQuery({
    queryKey: ['vehicles', 'units'],
    queryFn: () => get<UnitListResponse>('/society/units', { params: { limit: 500 } }),
    staleTime: 60_000
  });

  const all = paginator.items;
  const visible = useMemo(() => (kindFilter ? all.filter((v) => v.kind === kindFilter) : all), [all, kindFilter]);

  const byKind = useMemo(() => {
    const m: Record<VehicleKind, number> = { car: 0, bike: 0, cycle: 0, ev: 0, commercial: 0, other: 0 };
    for (const v of all) m[v.kind] += 1;
    return m;
  }, [all]);

  const form = useForm<CreateVehicleForm>({
    resolver: zodResolver(CreateVehicleSchema),
    defaultValues: { number_plate: '', kind: 'car', unit_id: '', make_model: '', color: '', parking_slot: '', notes: '' }
  });

  useEffect(() => {
    if (editing && open) {
      form.reset({
        number_plate: editing.number_plate,
        kind: editing.kind,
        unit_id: editing.unit_id ?? '',
        make_model: editing.make_model ?? '',
        color: editing.color ?? '',
        parking_slot: editing.parking_slot ?? '',
        notes: editing.notes ?? ''
      });
    } else if (!open) {
      form.reset({ number_plate: '', kind: 'car', unit_id: '', make_model: '', color: '', parking_slot: '', notes: '' });
    }
  }, [editing, open, form]);

  const create = useMutation({
    mutationFn: (v: CreateVehicleForm) =>
      post<Vehicle, Record<string, unknown>>('/vehicles', {
        number_plate: normalisePlateClient(v.number_plate),
        kind: v.kind,
        ...(v.unit_id ? { unit_id: v.unit_id } : {}),
        ...(v.make_model?.trim() ? { make_model: v.make_model } : {}),
        ...(v.color?.trim() ? { color: v.color } : {}),
        ...(v.parking_slot?.trim() ? { parking_slot: v.parking_slot } : {}),
        ...(v.notes?.trim() ? { notes: v.notes } : {})
      }),
    onSuccess: (created) => {
      toast.success(`Vehicle ${created.number_plate} registered`);
      void queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
      setOpen(false);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateVehicleForm }) =>
      patch<Vehicle>(`/vehicles/${id}`, {
        number_plate: normalisePlateClient(values.number_plate),
        kind: values.kind,
        make_model: values.make_model?.trim() ? values.make_model : null,
        color: values.color?.trim() ? values.color : null,
        parking_slot: values.parking_slot?.trim() ? values.parking_slot : null,
        notes: values.notes?.trim() ? values.notes : null
      }),
    onSuccess: (updated) => {
      toast.success(`Vehicle ${updated.number_plate} updated`);
      void queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
      setOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      patch<Vehicle>(`/vehicles/${id}`, { is_active }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/vehicles/${id}`),
    onSuccess: () => {
      toast.success('Vehicle removed');
      void queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onSubmit = (v: CreateVehicleForm): void => {
    if (editing) update.mutate({ id: editing.id, values: v });
    else create.mutate(v);
  };

  const closeDialog = (): void => {
    setOpen(false);
    setEditing(null);
  };

  const formDialog = (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button size="sm" onClick={() => setEditing(null)}>
          <Plus className="h-4 w-4" /> Add vehicle
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit vehicle' : 'Register vehicle'}</DialogTitle>
          <DialogDescription>
            Number plates are stored uppercase with no spaces (e.g. "ka 01 ab 1234" becomes "KA01AB1234").
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="number_plate">Number plate</Label>
              <Input id="number_plate" autoFocus placeholder="KA01AB1234" {...form.register('number_plate')} />
              {form.formState.errors.number_plate ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.number_plate.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="kind">Type</Label>
              <Select id="kind" {...form.register('kind')}>
                {(Object.keys(KIND_LABEL) as VehicleKind[]).map((k) => (
                  <option key={k} value={k}>{KIND_LABEL[k]}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="unit_id">Unit</Label>
            <Select id="unit_id" {...form.register('unit_id')}>
              <option value="">— Not assigned —</option>
              {(unitsQuery.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.block ? `${u.block.name}-` : ''}{u.number}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="make_model">Make / model</Label>
              <Input id="make_model" placeholder="Maruti Swift" {...form.register('make_model')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="color">Colour</Label>
              <Input id="color" placeholder="White" {...form.register('color')} />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="parking_slot">Parking slot</Label>
            <Input id="parking_slot" placeholder="B2-15" {...form.register('parking_slot')} />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="notes">Notes</Label>
            <Input id="notes" placeholder="Optional notes" {...form.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>Cancel</Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editing ? 'Saving…' : 'Adding…'}
            >
              {editing ? 'Save changes' : 'Register'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-vt-4">
      <div className="flex items-center justify-end">{formDialog}</div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <KpiCard label="Total" value={all.length} loading={query.isLoading} />
        <KpiCard label="Cars" value={byKind.car} loading={query.isLoading} />
        <KpiCard label="Bikes" value={byKind.bike} loading={query.isLoading} />
        <KpiCard label="Others" value={byKind.cycle + byKind.ev + byKind.commercial + byKind.other} loading={query.isLoading} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              placeholder="Plate, model, colour"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="kindFilter">Type</Label>
            <Select id="kindFilter" value={kindFilter} onChange={(e) => setKindFilter(e.target.value as VehicleKind | '')}>
              <option value="">All</option>
              {(Object.keys(KIND_LABEL) as VehicleKind[]).map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k]}</option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="activeFilter">Status</Label>
            <Select id="activeFilter" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}>
              <option value="all">All</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-vt-4">
          {query.isLoading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : query.isError ? (
            <EmptyState
              icon={<Car className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load vehicles"
              description={getErrorMessage(query.error)}
              action={<Button variant="secondary" onClick={() => void query.refetch()}>Try again</Button>}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<Car className="h-6 w-6" strokeWidth={1.75} />}
              title="No vehicles yet"
              description="Register the first vehicle for this society."
              action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add vehicle</Button>}
            />
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plate</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Colour / Model</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Resident</TableHead>
                  <TableHead>Slot</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="font-mono font-semibold text-vt-text">{v.number_plate}</TableCell>
                    <TableCell>
                      <Badge variant="muted">{KIND_LABEL[v.kind]}</Badge>
                    </TableCell>
                    <TableCell className="text-vt-text-muted">
                      {[v.color, v.make_model].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell>
                      {v.unit ? `${v.unit.block ? v.unit.block.name + '-' : ''}${v.unit.number}` : '—'}
                    </TableCell>
                    <TableCell className="text-vt-text-muted">{v.resident?.full_name ?? '—'}</TableCell>
                    <TableCell className="text-vt-text-muted">{v.parking_slot ?? '—'}</TableCell>
                    <TableCell>
                      {v.is_active ? <Badge variant="ok">Active</Badge> : <Badge variant="muted">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-vt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(v);
                            setOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive.mutate({ id: v.id, is_active: !v.is_active })}
                        >
                          {v.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoveTarget(v)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={query.isFetching}
              itemsShown={visible.length}
              pageSize={50}
              onLoadMore={() => {
                if (paginator.nextCursor) setCursor(paginator.nextCursor);
              }}
            />
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Remove vehicle"
        description={
          removeTarget ? (
            <>
              Remove vehicle <strong>{removeTarget.number_plate}</strong> from the registry?
            </>
          ) : null
        }
        confirmLabel="Remove vehicle"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </div>
  );
}

function EntriesTab(): JSX.Element {
  const [status, setStatus] = useState<'' | VehicleEntryStatus>('');
  const [actor, setActor] = useState<'' | VehicleEntryActor>('');
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['vehicles', 'entries', status, actor, search] as const, [status, actor, search]);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      get<EntryListResponse>('/vehicle-entries', {
        params: {
          limit: 200,
          ...(status ? { status } : {}),
          ...(actor ? { actor_kind: actor } : {}),
          ...(search ? { q: search } : {})
        }
      })
  });

  useEffect(() => {
    const offs = (['vehicle:entry', 'vehicle:exit'] as const).map((evt) =>
      subscribe(evt, () => {
        void queryClient.invalidateQueries({ queryKey: ['vehicles', 'entries'] });
      })
    );
    return () => offs.forEach((f) => f());
  }, [queryClient]);

  const exit = useMutation({
    mutationFn: (id: string) => post<VehicleEntry>(`/vehicle-entries/${id}/exit`),
    onSuccess: () => {
      toast.success('Exit logged');
      void queryClient.invalidateQueries({ queryKey: ['vehicles', 'entries'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const items = query.data?.items ?? [];
  const insideCount = query.data?.inside_count ?? 0;

  return (
    <div className="space-y-vt-4">
      {/* KPI */}
      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-3">
        <KpiCard label="Currently inside" value={insideCount} loading={query.isLoading} accent="alert" />
        <KpiCard label="Entries shown" value={items.length} loading={query.isLoading} />
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-4 text-vt-13 text-vt-text-muted">
            <Radio className="h-4 w-4 text-vt-primary" />
            Live updates via realtime socket
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="esearch">Search</Label>
            <Input id="esearch" placeholder="Plate or visitor name" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="estatus">Status</Label>
            <Select id="estatus" value={status} onChange={(e) => setStatus(e.target.value as VehicleEntryStatus | '')}>
              <option value="">All</option>
              <option value="inside">Inside</option>
              <option value="exited">Exited</option>
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="eactor">Actor</Label>
            <Select id="eactor" value={actor} onChange={(e) => setActor(e.target.value as VehicleEntryActor | '')}>
              <option value="">All</option>
              {(Object.keys(ACTOR_LABEL) as VehicleEntryActor[]).map((a) => (
                <option key={a} value={a}>{ACTOR_LABEL[a]}</option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Live entries table */}
      <Card>
        <CardContent className="p-vt-4">
          {query.isLoading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : query.isError ? (
            <EmptyState
              icon={<LogIn className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load entries"
              description={getErrorMessage(query.error)}
              action={<Button variant="secondary" onClick={() => void query.refetch()}>Try again</Button>}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<LogIn className="h-6 w-6" strokeWidth={1.75} />}
              title="No entries yet"
              description="Vehicle entries logged by the guard will appear here in realtime."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plate</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Visitor / Unit</TableHead>
                  <TableHead>Gate</TableHead>
                  <TableHead>Entered</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono font-semibold text-vt-text">{e.number_plate}</TableCell>
                    <TableCell><Badge variant="muted">{ACTOR_LABEL[e.actor_kind]}</Badge></TableCell>
                    <TableCell className="text-vt-text-muted">
                      {e.visitor_name ?? '—'}
                      {e.unit ? ` · ${e.unit.block ? e.unit.block.name + '-' : ''}${e.unit.number}` : ''}
                    </TableCell>
                    <TableCell className="text-vt-text-muted">{e.gate?.name ?? '—'}</TableCell>
                    <TableCell className="text-vt-text-muted">{formatDateTime(e.entered_at)}</TableCell>
                    <TableCell>
                      {e.status === 'inside' ? (
                        <Badge variant="info">Inside</Badge>
                      ) : (
                        <Badge variant="muted">Exited · {e.exited_at ? formatDateTime(e.exited_at) : ''}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {e.status === 'inside' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => exit.mutate(e.id)}
                          loading={exit.isPending && exit.variables === e.id}
                          loadingText="Exiting…"
                        >
                          <LogOut className="h-4 w-4" /> Mark exit
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  loading,
  accent
}: {
  label: string;
  value: number;
  loading?: boolean;
  accent?: 'alert';
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span
          className={
            accent === 'alert'
              ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-alert-100 text-vt-alert'
              : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary'
          }
        >
          <Car className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-24 font-semibold text-vt-text">
            {loading ? <Skeleton className="h-7 w-12" /> : value}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
